// @ts-nocheck
/**
 * Manmukh Ayurveda Enterprise Framework
 * File: assets/ma-rotating-card-ring.js
 * Parametric elliptical rotating card gallery — production build.
 *
 * Ported per §25B from the Reference Package version analyzed in Phase 3
 * (recommendation: REUSE). This file had zero missing dependencies and no
 * hardcoded values needing correction — logic below is unchanged from the
 * source; only this header was added for governance traceability.
 *
 * GEOMETRY
 *   x(θ) = rx · cos(θ)          horizontal position
 *   z(θ) = rz · sin(θ)          depth (toward / away camera)
 *   θᵢ   = globalRotation + baseAngle_i
 *
 *   Tangent → scaleX foreshortening (face-on = full width, edge-on = sliver)
 *   Camera perspective → scaleY + vertical nudge
 *   Depth-sorted z-index: cached depths array, sort once per frame, no DOM reads
 *
 * FEATURES
 *   ✔ Auto-rotation with smooth velocity lerp
 *   ✔ Pointer drag (mouse + touch) with commit-threshold, inertia, momentum decay
 *   ✔ Wheel / trackpad horizontal scroll to rotate
 *   ✔ Keyboard: ArrowLeft / ArrowRight step; Home / End jump; Space toggle autoplay
 *   ✔ ARIA: region, roledescription="carousel", live status announcements
 *   ✔ Focus-aware: ring pauses while keyboard focus is inside the viewport
 *   ✔ Reduced-motion: honours prefers-reduced-motion (live MediaQueryList listener)
 *   ✔ IntersectionObserver + visibilitychange → rAF paused when off-screen / tab hidden
 *   ✔ Multi-instance safe — registry keyed by section id
 *   ✔ Theme Editor safe — shopify:section:load / :unload lifecycle
 *   ✔ No external runtime dependency
 *
 * EXTENSIBILITY
 *   The geometry is separated into _computeGeometry() + layoutAll().
 *   Future presentation modes (coverflow, 3D tilt, flat grid) can override
 *   layoutAll() without touching interaction or lifecycle code.
 */
(function () {
  'use strict';

  /* ── Registry ── */
  var REGISTRY = new Map();

  /* ── Helpers ── */
  var RAD = Math.PI / 180;

  /* Fixed keyboard rotation increment (degrees). There is no merchant
     control for this, and the section never emitted a `data-key-step`
     attribute, so the previous `parseFloat(ds.keyStep) || 30` read was
     dead code that always resolved to 30. */
  var KEY_STEP = 30;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  function pointerX(e) { return e.touches ? e.touches[0].clientX : e.clientX; }
  function pointerY(e) { return e.touches ? e.touches[0].clientY : e.clientY; }

  /* ─────────────────────────────────────────────────────────
     MaRing constructor
  ───────────────────────────────────────────────────────── */
  function MaRing(section) {
    this.section  = section;
    this.viewport = section.querySelector('[data-ring-viewport]');
    this.ring     = section.querySelector('[data-ring]');
    if (!this.viewport || !this.ring) return;

    var scope = section.closest(".shopify-section");
    var marker = scope && scope.querySelector(".ma-card-engine-scope");
    this.cardDesign = marker ? marker.dataset.style : "legacy";
    var ds = this.viewport.dataset;
    this.opts = {
      dragEnabled:     ds.dragEnabled     !== 'false',
      /* Fallback matches the Liquid schema default for `drag_sensitivity`
         (16, emitted as 16/100) so a missing/blank attribute behaves the
         same as the merchant default instead of a stronger 0.25. */
      dragSensitivity: parseFloat(ds.dragSensitivity) || 0.16,
      depthIntensity:  clamp(Number.isFinite(parseFloat(ds.depthIntensity)) ? parseFloat(ds.depthIntensity) : 0.5, 0, 1),
      autoRotate:      ds.autoRotate      !== 'false',
      axis:            ds.rotationAxis    || 'y',
      radiusScale:     parseFloat(ds.radiusScale)     || 1
    };

    if (this.opts.axis === 'x') {
      this.viewport.style.touchAction = 'pan-x';
    }

    /* — Card collection — */
    this.cards  = [];
    this.count  = 0;
    this.geo    = {};
    this.depths = [];
    this.order  = [];

    /* — Animation state — */
    this.rotation    = 0;
    this.velocity    = 0;           // deg/sec
    this.lastFrameT  = null;
    this.rafId       = null;
    this.running     = false;
    this.inView      = true;
    this.hasFocus    = false;
    this.reduced     = prefersReducedMotion();
    this.autoRotate  = this.opts.autoRotate && !this.reduced;
    this.AUTO_SPEED  = 14;
    this.resumeTimer = null;

    /* — Drag state — */
    this.isDragging      = false;
    this.gestureCommitted= false;
    this.gestureRejected = false;
    this.startX          = 0;
    this.startY          = 0;
    this.dragStartRot    = 0;
    this.lastX           = 0;
    this.lastY           = 0;
    this.lastT           = 0;
    this.dragMoved       = false;
    this.COMMIT_THRESHOLD= 6;       // px before gesture is locked in

    /* — ARIA live region — */
    this._liveEl = null;

    /* — Localized live-region announcements (A11Y-03) —
       Emitted by sections/ma-rotating-card-ring.liquid from the
       `ma.rotating_ring.announce_*` locale keys so screen-reader output
       follows the storefront language instead of being hardcoded English.
       The English literals below are last-resort fallbacks only, for the
       case where the markup predates these attributes. */
    this.strings = {
      left:    ds.i18nRotateLeft    || 'Rotating left',
      right:   ds.i18nRotateRight   || 'Rotating right',
      reset:   ds.i18nReset         || 'Ring reset to start',
      opposite:ds.i18nOpposite      || 'Ring at opposite side',
      resumed: ds.i18nResumed       || 'Rotation resumed',
      stopped: ds.i18nStopped       || 'Rotation stopped'
    };

    /* — Resize debounce — */
    this._resizeTimer = null;

    /* — Reduced motion media query (live) — */
    if (window.matchMedia) {
      this._rmq = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._onReducedMotion = this._onReducedMotion.bind(this);
      try { this._rmq.addEventListener('change', this._onReducedMotion); }
      catch (e) { this._rmq.addListener(this._onReducedMotion); }
    }

    /* — Phase 2: master 3D toggle (Theme Editor > Manmukh Ayurveda Brand >
       "Enable 3D Material & Motion Effects"). When off, this constructor
       never starts the rAF engine, never attaches drag/wheel listeners,
       and never computes elliptical geometry — [data-ma-3d="off"] in
       ma-rotating-card-ring.css replaces the ring with a native
       horizontal scroll-snap row instead, so there is zero heavy JS
       running, not just heavy JS that's hidden. See ma-3d.js. — */
    this.threeDEnabled = !!(
      window.ManmukhAyurveda &&
      typeof window.ManmukhAyurveda.is3DEnabled === 'function' &&
      window.ManmukhAyurveda.is3DEnabled()
    );

    this._bind();
    this._buildARIA();
    this._collectCards();
    this._attachControls();
    this._computeGeometry();

    if (this.threeDEnabled) {
      this.section.classList.add('ma-ring--enhanced');
      this.layoutAll(0);
      this._observeVisibility();
      this._attachEvents();
      this._start();
    } else {
      this._attachFlatEvents();
    }
  }

  /* ── Bind hot-path methods once ── */
  MaRing.prototype._syncControls = function () {
    var button = this.section.querySelector('[data-ring-toggle]');
    if (!button) return;
    /* The toggle represents the chosen autoplay state. Keyboard focus pauses
       motion temporarily; it must not reverse the advertised button action. */
    var playing = this.autoRotate && !this.reduced;
    button.textContent = playing ? button.dataset.pauseLabel : button.dataset.playLabel;
    button.setAttribute('aria-pressed', String(!!playing));
    button.disabled = this.reduced || !this.threeDEnabled;
  };

  MaRing.prototype._attachControls = function () {
    var self = this;
    this._controlHandler = function (e) {
      var target = e.target.closest('[data-ring-previous], [data-ring-next], [data-ring-toggle]');
      if (!target) return;
      if (target.hasAttribute('data-ring-toggle')) {
        self.autoRotate = !self.autoRotate && !self.reduced;
        self.userPlaying = self.autoRotate;
        self.userPaused = !self.autoRotate;
        self._clearResume();
        if (!self.autoRotate) self.velocity = 0;
        self._announce(self.autoRotate ? self.strings.resumed : self.strings.stopped);
        self._syncControls();
        self._start();
      } else if (self.threeDEnabled) {
        self._step((target.hasAttribute('data-ring-next') ? -1 : 1) * 360 / Math.max(1, self.count));
      } else {
        self.viewport.scrollBy({left: target.hasAttribute('data-ring-next') ? 220 : -220, behavior: self.reduced ? 'auto' : 'smooth'});
      }
    };
    this.section.addEventListener('click', this._controlHandler);
    this._syncControls();
  };

  MaRing.prototype._bind = function () {
    this._tick            = this._tick.bind(this);
    this._onPointerDown   = this._onPointerDown.bind(this);
    this._onPointerMove   = this._onPointerMove.bind(this);
    this._onPointerUp     = this._onPointerUp.bind(this);
    this._onPointerCancel = this._onPointerCancel.bind(this);
    this._onPointerLeave  = this._onPointerLeave.bind(this);
    this._onResize        = this._onResize.bind(this);
    this._onVisibility    = this._onVisibility.bind(this);
    this._onClickCapture  = this._onClickCapture.bind(this);
    this._onKeyDown       = this._onKeyDown.bind(this);
    this._onWheel         = this._onWheel.bind(this);
    this._onFocusIn       = this._onFocusIn.bind(this);
    this._onFocusOut      = this._onFocusOut.bind(this);
    this._onFlatKeyDown   = this._onFlatKeyDown.bind(this);
  };

  /* ── ARIA setup ── */
  MaRing.prototype._buildARIA = function () {
    /* Off-screen live region for announcements */
    var live = document.createElement('div');
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('aria-atomic', 'true');
    live.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;';
    this.viewport.appendChild(live);
    this._liveEl = live;
  };

  MaRing.prototype._announce = function (msg) {
    if (!this._liveEl) return;
    this._liveEl.textContent = '';
    /* Flush, then set — screen readers only fire on change */
    var self = this;
    requestAnimationFrame(function () { self._liveEl.textContent = msg; });
  };

  /* ── Collect card elements ── */
  MaRing.prototype._collectCards = function () {
    var els = this.ring.querySelectorAll('[data-ring-card]');
    this.cards = [];
    for (var i = 0; i < els.length; i++) {
      this.cards.push({ el: els[i], baseAngle: 0 });
    }
    this.count  = this.cards.length;
    this.depths = new Array(this.count);
    this.order  = new Array(this.count);
    var step = this.count > 0 ? 360 / this.count : 0;
    for (var j = 0; j < this.count; j++) {
      this.cards[j].baseAngle = j * step;
      this.order[j] = j;
    }
  };

  /* ── Responsive geometry ── */
  MaRing.prototype._computeGeometry = function () {
    var rect = this.viewport.getBoundingClientRect();
    var vw   = rect.width  || this.viewport.offsetWidth  || 1;
    var vh   = rect.height || this.viewport.offsetHeight || 1;

    var cfg;
    if (vw < 640) {
      cfg = {
        cx: vw * 0.5, cy: vh * 0.70,
        rx: vw * 0.95, rz: vw * 0.65,
        cardW: clamp(vw * 0.28, 80,  120),
        cardH: clamp(vw * 0.28 * 1.45, 116, 174),
        camDist: vh * 1.6,  tilt: 0.10
      };
    } else if (vw < 1100) {
      cfg = {
        cx: vw * 0.5, cy: vh * 0.68,
        rx: vw * 0.7, rz: vw * 0.45,
        cardW: clamp(vw * 0.18, 110,  160),
        cardH: clamp(vw * 0.18 * 1.45, 159, 232),
        camDist: vh * 1.8,  tilt: 0.12
      };
    } else {
      cfg = {
        cx: vw * 0.5, cy: vh * 0.65,
        rx: vw * 0.5, rz: vw * 0.35,
        cardW: clamp(vw * 0.14, 140, 200),
        cardH: clamp(vw * 0.14 * 1.45, 203, 290),
        camDist: vh * 2.0,  tilt: 0.15
      };
    }
    /* New artwork/caption designs need real room for whole disease names.
       Legacy geometry stays unchanged. Scale remains inside viewport bounds. */
    if (this.cardDesign !== 'legacy' && vw < 700) {
      cfg.cardW = clamp(vw * 0.46, 144, 176);
      cfg.cardH = clamp(vh * 0.72, 190, 232);
    }
    this.geo = { vw: vw, vh: vh };
    for (var k in cfg) { if (Object.prototype.hasOwnProperty.call(cfg, k)) this.geo[k] = cfg[k]; }
    this.geo.rx *= this.opts.radiusScale;
    this.geo.rz *= this.opts.radiusScale;
    if (this.opts.axis === 'x') {
      this.geo.cy = vh * 0.5;
      this.geo.rx = Math.min(this.geo.rx, Math.max(0, (vh - this.geo.cardH * 1.4) / 2));
      this.geo.rz = Math.min(this.geo.rz, this.geo.camDist * 0.35);
    }
    this.ring.style.setProperty('--ring-card-w', this.geo.cardW + 'px');
    this.ring.style.setProperty('--ring-card-h', this.geo.cardH + 'px');
  };

  /* ── Per-frame layout — no DOM reads in hot path ── */
  MaRing.prototype.layoutAll = function (globalRotDeg) {
    var g    = this.geo;
    if (!g.rz) return;
    var rot      = globalRotDeg * RAD;
    var depthAmt = this.opts.depthIntensity;
    var cards    = this.cards;

    for (var i = 0; i < cards.length; i++) {
      var c     = cards[i];
      var theta = rot + c.baseAngle * RAD;
      var cosT  = Math.cos(theta);
      var sinT  = Math.sin(theta);

      var ex = this.opts.axis === 'x' ? 0 : g.rx * cosT;
      var ey = this.opts.axis === 'x' ? g.rx * cosT : 0;
      var ez = g.rz * sinT;

      /* Perspective & Foreshortening adjustments for premium fanned look */
      var tx  = this.opts.axis === 'x' ? 0 : -g.rx * sinT;
      var ty  = this.opts.axis === 'x' ? -g.rx * sinT : 0;
      var tz  =  g.rz * cosT;
      var tLen  = Math.hypot(this.opts.axis === 'x' ? ty : tx, tz) || 1;
      var facing = Math.abs(tz) / tLen;

      var scaleForeshorten = Math.max(0.55, 1 - facing * 0.45);
      var scaleX = this.opts.axis === 'x' ? 1 : scaleForeshorten;
      var scaleY = this.opts.axis === 'x' ? scaleForeshorten : 1;

      var persp  = g.camDist / (g.camDist - ez);
      scaleX *= clamp(persp, 0.45, 1.25);
      scaleY *= clamp(persp, 0.45, 1.25);

      /* Premium focal hierarchy: gracefully enlarge the front card */
      var heroBoost = 1 + Math.pow(Math.max(0, -ez) / g.rz, 2) * 0.18;
      scaleX *= heroBoost;
      scaleY *= heroBoost;

      var screenX = g.cx + ex;
      var screenY = g.cy - ez * g.tilt - (persp - 1) * 32 + ey;
      var halfHeight = g.cardH * scaleY / 2;
      screenY = clamp(screenY, halfHeight + 8, g.vh - halfHeight - 8);

      this.depths[i] = ez;

      /* Single transform string — GPU-composited */
      c.el.style.transform =
        'translate(' + screenX.toFixed(1) + 'px,' + screenY.toFixed(1) + 'px)' +
        ' scale(' + scaleX.toFixed(4) + ',' + scaleY.toFixed(4) + ')';

      /* Depth opacity */
      var baseOpa = (ez / g.rz + 1) / 2;
      if (this.cardDesign !== "legacy") {
        /* Transparent surfaces expose rear captions through front artwork.
           Link names remain in HTML; keyboard focus brings its card forward. */
        c.el.style.setProperty("--ce-caption-opacity", baseOpa < 0.5 ? "0" : "1");
        c.el.style.setProperty("--ce-art-opacity", (1 - depthAmt + depthAmt * (0.18 + 0.82 * baseOpa)).toFixed(3));
      }
      c.el.style.opacity = (1 - depthAmt + depthAmt * (0.5 + 0.5 * baseOpa)).toFixed(3);

      this.order[i] = i;
    }

    /* Z-index sort — one typed sort on cached depths */
    var depths = this.depths;
    this.order.sort(function (a, b) { return depths[a] - depths[b]; });
    for (var rank = 0; rank < this.order.length; rank++) {
      cards[this.order[rank]].el.style.zIndex = rank;
    }
  };

  /* ── rAF tick ── */
  MaRing.prototype._tick = function (t) {
    if (!this.running) return;
    if (this.lastFrameT == null) this.lastFrameT = t;
    var dt = clamp(t - this.lastFrameT, 0, 48) / 1000;
    this.lastFrameT = t;

    if (!this.isDragging) {
      if (this.autoRotate && !this.reduced && (!this.hasFocus || this.userPlaying)) {
        this.velocity += (this.AUTO_SPEED - this.velocity) * Math.min(1, dt * 2.2);
      } else {
        /* Smooth, natural physical momentum decay */
        this.velocity *= Math.pow(0.15, dt);
        if (Math.abs(this.velocity) < 0.2) this.velocity = 0;
      }
      this.rotation += this.velocity * dt;
    }

    this.layoutAll(this.rotation);
    if (!this.isDragging && this.velocity === 0 && (!this.autoRotate || this.hasFocus && !this.userPlaying)) {
      this._stop();
      return;
    }
    this.rafId = requestAnimationFrame(this._tick);
  };

  /* ── Auto-rotation gating (MA-RING-01) ──
     Idle auto-rotation may only ever resume on its own when the merchant
     actually enabled it AND the visitor is not asking for reduced motion.
     Previously _endDrag(), _onWheel() and _step() each set
     `autoRotate = true` unconditionally after ~1600 ms, so the very first
     drag, horizontal wheel gesture or arrow keypress silently overrode a
     merchant "Enable auto-rotation = OFF" setting for the rest of the
     page session. Every deferred resume now goes through
     _scheduleAutoResume(), which is a no-op when auto-rotation is not
     permitted — the timer is simply never armed. The Space-key control in
     _onKeyDown() is deliberately left as a direct toggle: that is an
     explicit user play/pause action, not an automatic override. */
  MaRing.prototype._canAutoRotate = function () {
    return !!(this.opts.autoRotate && !this.reduced && !this.userPaused);
  };

  MaRing.prototype._clearResume = function () {
    if (this.resumeTimer) {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = null;
    }
  };

  MaRing.prototype._scheduleAutoResume = function () {
    this._clearResume();
    if (!this._canAutoRotate()) return;
    var self = this;
    this.resumeTimer = setTimeout(function () {
      self.resumeTimer = null;
      self.autoRotate = true;
      self._syncControls();
      self._start();
    }, 1600);
  };

  /* ── Start / Stop ── */
  MaRing.prototype._start = function () {
    if (this.running) return;
    if (this.reduced || !this.threeDEnabled) return;
    if (!this.inView || document.hidden) return;
    this.running    = true;
    this.lastFrameT = null;
    this.rafId      = requestAnimationFrame(this._tick);
  };

  MaRing.prototype._stop = function () {
    this.running = false;
    if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = null; }
  };

  /* ── Pointer / Touch drag ── */
  MaRing.prototype._onPointerDown = function (e) {
    if (!this.opts.dragEnabled || e.target.closest('video, button')) return;
    this.isDragging       = true;
    this.gestureCommitted = false;
    this.gestureRejected  = false;
    this.dragMoved        = false;
    this.autoRotate       = false;
    this._syncControls();
    this.velocity         = 0;
    this._clearResume();
    this.startX  = this.lastX = pointerX(e);
    this.startY  = this.lastY = pointerY(e);
    this.dragStartRot = this.rotation;
    this.lastT   = performance.now();
  };

  MaRing.prototype._onPointerMove = function (e) {
    if (!this.isDragging || this.gestureRejected) return;
    var x  = pointerX(e);
    var y  = pointerY(e);
    var dx = x - this.startX;
    var dy = y - this.startY;

    if (!this.gestureCommitted) {
      if (Math.abs(dx) < this.COMMIT_THRESHOLD && Math.abs(dy) < this.COMMIT_THRESHOLD) return;
      var intendedDrag = this.opts.axis === 'x' ? Math.abs(dy) > Math.abs(dx) : Math.abs(dx) > Math.abs(dy);
      
      if (intendedDrag) {
        this.gestureCommitted = true;
        this.viewport.classList.add('is-grabbing');
        try { this.viewport.setPointerCapture(e.pointerId); } catch (err) {}
      } else {
        this.gestureRejected = true;
        this.isDragging = false;
        return;
      }
    }

    if (e.cancelable) e.preventDefault();
    this.dragMoved = true;

    var now = performance.now();
    var dtms = Math.max(1, now - this.lastT);
    
    var deltaPx = this.opts.axis === 'x' ? (y - this.lastY) : (x - this.lastX);
    var instDeg = deltaPx * this.opts.dragSensitivity;
    
    this.rotation = this.dragStartRot + (this.opts.axis === 'x' ? dy : dx) * this.opts.dragSensitivity;
    /* Blend velocity for smoother throwing */
    this.velocity = this.velocity * 0.4 + ((instDeg / dtms) * 1000) * 0.6;

    this.lastX = x;
    this.lastY = y;
    this.lastT = now;
    this.layoutAll(this.rotation);
    this._start();
  };

  MaRing.prototype._endDrag = function () {
    if (!this.isDragging) return;
    this.isDragging       = false;
    this.gestureCommitted = false;
    this.viewport.classList.remove('is-grabbing');
    if (this.reduced) this.velocity = 0;
    this._start();

    this._scheduleAutoResume();
  };

  MaRing.prototype._onPointerUp     = function ()  { this._endDrag(); };
  MaRing.prototype._onPointerCancel = function ()  { this._endDrag(); };
  MaRing.prototype._onPointerLeave  = function ()  { if (this.gestureCommitted) this._endDrag(); };

  /* ── Prevent navigation after real drag ── */
  MaRing.prototype._onClickCapture = function (e) {
    if (this.dragMoved) {
      e.preventDefault();
      e.stopPropagation();
      this.dragMoved = false;
    }
  };

  /* ── Wheel / trackpad horizontal scroll ── */
  /*
    Wheel-hijack fix (Phase 3 continuation remediation): previously, for
    the default axis:'y' ring, this fell back to `-e.deltaY` whenever
    deltaX was zero -- true for virtually every ordinary vertical
    mouse-wheel scroll, since deltaX only carries a nonzero value for a
    genuine horizontal-scroll gesture (shift+wheel, a trackpad
    two-finger horizontal swipe, or a horizontal-scroll mouse). That
    meant any normal attempt to scroll the page vertically while the
    cursor happened to be over the ring was captured and
    preventDefault()'d, blocking page scroll entirely -- for either axis
    setting, since the axis:'x' branch had the same problem in reverse
    (defaulting to deltaY, which ordinary vertical scrolling always
    supplies). Only a genuine horizontal delta is now treated as
    rotate-intent, matching this file's own documented feature ("Wheel /
    trackpad horizontal scroll to rotate" in the header above) --
    deltaY-only events, the overwhelming majority of real scrolling, now
    fall straight through to the browser's normal page scroll, exactly
    as any other page content would behave.
  */
  MaRing.prototype._onWheel = function (e) {
    var delta = e.deltaX;
    if (Math.abs(delta) < 2) return;
    if (e.cancelable) e.preventDefault();
    this.rotation += delta * (this.opts.dragSensitivity * 0.5);
    this.velocity  = delta * this.opts.dragSensitivity * 12;

    this.autoRotate = false;
    this.layoutAll(this.rotation);
    this._syncControls();
    this._start();
    this._scheduleAutoResume();
  };

  /* ── Keyboard navigation ── */
  MaRing.prototype._onKeyDown = function (e) {
    var step = KEY_STEP;
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        this._step(step);
        this._announce(this.strings.left);
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        this._step(-step);
        this._announce(this.strings.right);
        break;
      case 'Home':
        e.preventDefault();
        this.rotation = 0;
        this._announce(this.strings.reset);
        break;
      case 'End':
        e.preventDefault();
        this.rotation = 180;
        this._announce(this.strings.opposite);
        break;
      case ' ':
        e.preventDefault();
        this.autoRotate = !this.autoRotate && !this.reduced;
        this.userPlaying = this.autoRotate;
        this.userPaused = !this.autoRotate;
        this._clearResume();
        if (!this.autoRotate) this.velocity = 0;
        this._announce(this.autoRotate ? this.strings.resumed : this.strings.stopped);
        break;
    }
    this.layoutAll(this.rotation);
    this._syncControls();
    this._start();
  };

  MaRing.prototype._step = function (deg) {
    this.velocity   = 0;
    this.rotation  += deg;
    /* Brief moment of velocity for smooth glide */
    this.velocity   = deg * 4;

    this.autoRotate = false;
    this.userPlaying = false;
    this._syncControls();
    if (this.reduced) this.velocity = 0;
    this.layoutAll(this.rotation);
    this._start();
    this._scheduleAutoResume();
  };

  /* ── Focus awareness (pause auto-rotate while keyboard focus inside) ── */
  MaRing.prototype._onFocusIn = function (event) {
    this.hasFocus = true;
    this.userPlaying = false;
    if (this.cardDesign !== "legacy" && event && event.target && event.target.matches(":focus-visible")) {
      var focusedCard = event.target.closest("[data-ring-card]");
      for (var i = 0; focusedCard && i < this.cards.length; i++) {
        if (this.cards[i].el === focusedCard) {
          this.rotation = 90 - this.cards[i].baseAngle;
          this.velocity = 0;
          this.layoutAll(this.rotation);
          break;
        }
      }
    }
    this._syncControls();
  };
  MaRing.prototype._onFocusOut = function () { this.hasFocus = false; this._syncControls(); this._start(); };

  /* ── Resize ── */
  MaRing.prototype._onResize = function () {
    var self = this;
    clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(function () {
      self._computeGeometry();
      self.layoutAll(self.rotation);
    }, 120);
  };

  /* ── Tab visibility ── */
  MaRing.prototype._onVisibility = function () {
    if (document.hidden) { this._stop(); }
    else if (this.inView) { this._start(); }
  };

  /*
    Reduced-motion CPU-loop fix (Phase 3 continuation remediation):
    previously this only zeroed the velocity variable, never actually
    stopping the rAF loop -- _tick() kept unconditionally rescheduling
    itself via requestAnimationFrame every frame regardless of reduced-
    motion state, so a user with prefers-reduced-motion enabled still
    got a continuously-running rAF loop recomputing and rewriting every
    card's transform/opacity every frame for no visible change, a
    wasteful, always-on CPU cost this setting is supposed to eliminate.
    _stop() actually cancels the scheduled frame. If the OS-level setting
    is later toggled back off while 3D effects are enabled, _start() is
    called to resume -- it already no-ops safely if the ring is out of
    view or the tab is hidden, exactly like the existing
    IntersectionObserver/visibilitychange callbacks below.
  */
  MaRing.prototype._onReducedMotion = function (mql) {
    this.reduced = mql.matches;
    if (this.reduced) {
      /* Also cancel any pending auto-resume and clear the flag, so a timer
         armed before the setting changed cannot restart rotation. */
      this._clearResume();
      this.autoRotate = false;
      this.velocity = 0;
      this._stop();
    } else if (this.threeDEnabled) {
      this.autoRotate = this._canAutoRotate();
      this._start();
    }
    this._syncControls();
  };

  /* ── IntersectionObserver ── */
  MaRing.prototype._observeVisibility = function () {
    var self = this;
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          self.inView = entries[i].isIntersecting;
          if (self.inView) self._start(); else self._stop();
        }
      }, { threshold: 0.01 });
      this.io.observe(this.viewport);
    }
  };

  /* ── Flat-mode events (3D toggle OFF) ──
     No rAF loop, no drag/wheel/perspective geometry — the browser's own
     scroll-snap handles pointer/touch interaction (see the
     [data-ma-3d="off"] rules in ma-rotating-card-ring.css). The only
     thing JS still owns here is arrow-key navigation, so keyboard users
     lose nothing when the merchant turns 3D effects off. */
  MaRing.prototype._attachFlatEvents = function () {
    this.viewport.addEventListener('keydown', this._onFlatKeyDown);
  };

  MaRing.prototype._detachFlatEvents = function () {
    if (this.viewport) this.viewport.removeEventListener('keydown', this._onFlatKeyDown);
  };

  MaRing.prototype._onFlatKeyDown = function (e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    var card = this.viewport.querySelector('[data-ring-card]');
    var step = (card && card.getBoundingClientRect().width || 200) + 16;
    this.viewport.scrollBy({
      left: e.key === 'ArrowRight' ? step : -step,
      behavior: this.reduced ? 'auto' : 'smooth'
    });
  };

  /* ── Attach all event listeners ── */
  MaRing.prototype._attachEvents = function () {
    var vp = this.viewport;

    /* Pointer (unified mouse + touch) */
    vp.addEventListener('pointerdown',   this._onPointerDown,   { passive: true });
    vp.addEventListener('pointermove',   this._onPointerMove,   { passive: false });
    vp.addEventListener('pointerup',     this._onPointerUp,     { passive: true });
    vp.addEventListener('pointercancel', this._onPointerCancel, { passive: true });
    vp.addEventListener('pointerleave',  this._onPointerLeave,  { passive: true });
    vp.addEventListener('click',         this._onClickCapture,  true);

    /* Wheel */
    vp.addEventListener('wheel', this._onWheel, { passive: false });

    /* Keyboard — viewport must be focusable (tabindex set in liquid) */
    vp.addEventListener('keydown',   this._onKeyDown);
    vp.addEventListener('focusin',   this._onFocusIn);
    vp.addEventListener('focusout',  this._onFocusOut);

    /* Global */
    window.addEventListener('resize',           this._onResize);
    document.addEventListener('visibilitychange', this._onVisibility);
  };

  /* ── Destroy — full teardown ── */
  MaRing.prototype.destroy = function () {
    this._stop();
    this.section.removeEventListener('click', this._controlHandler);
    this.section.classList.remove('ma-ring--enhanced');
    var vp = this.viewport;
    if (this.threeDEnabled) {
      if (vp) {
        vp.removeEventListener('pointerdown',   this._onPointerDown);
        vp.removeEventListener('pointermove',   this._onPointerMove);
        vp.removeEventListener('pointerup',     this._onPointerUp);
        vp.removeEventListener('pointercancel', this._onPointerCancel);
        vp.removeEventListener('pointerleave',  this._onPointerLeave);
        vp.removeEventListener('click',         this._onClickCapture, true);
        vp.removeEventListener('wheel',         this._onWheel);
        vp.removeEventListener('keydown',       this._onKeyDown);
        vp.removeEventListener('focusin',       this._onFocusIn);
        vp.removeEventListener('focusout',      this._onFocusOut);
      }
      window.removeEventListener('resize',            this._onResize);
      document.removeEventListener('visibilitychange', this._onVisibility);
      if (this.io) { this.io.disconnect(); this.io = null; }
      clearTimeout(this._resizeTimer);
    } else {
      this._detachFlatEvents();
    }
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    if (this._rmq) {
      try { this._rmq.removeEventListener('change', this._onReducedMotion); }
      catch (e) { this._rmq.removeListener(this._onReducedMotion); }
    }
  };

  /* ─────────────────────────────────────────────────────────
     Bootstrap
  ───────────────────────────────────────────────────────── */
  function initSection(section) {
    if (!section) return;
    var id = section.dataset.sectionId || section.id;
    if (REGISTRY.has(id)) { REGISTRY.get(id).destroy(); REGISTRY.delete(id); }
    var inst = new MaRing(section);
    if (inst.viewport) REGISTRY.set(id, inst);
  }

  function initAll() {
    var sections = document.querySelectorAll('[data-ma-ring]');
    for (var i = 0; i < sections.length; i++) initSection(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Shopify Theme Editor lifecycle */
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector('[data-ma-ring]');
    if (s) initSection(s);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var s = e.target.querySelector('[data-ma-ring]');
    if (s) {
      var id = s.dataset.sectionId || s.id;
      if (REGISTRY.has(id)) { REGISTRY.get(id).destroy(); REGISTRY.delete(id); }
    }
  });
})();

